import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

const blank = { name: "", description: "" };

export default function CostShops() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(blank);

    const load = async () => setItems((await api.get("/admin/cost-shops")).data);
    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
    const openEdit = (c) => {
        setEditing(c);
        setForm({ name: c.name, description: c.description || "" });
        setOpen(true);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editing) {
                await api.patch(`/admin/cost-shops/${editing.id}`, form);
                toast.success("Cost shop updated");
            } else {
                await api.post("/admin/cost-shops", form);
                toast.success("Cost shop added");
            }
            setOpen(false);
            setEditing(null);
            setForm(blank);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete cost shop?")) return;
        await api.delete(`/admin/cost-shops/${id}`); load();
    };

    return (
        <div className="p-8 max-w-4xl" data-testid="cost-shops-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="text-xs tracking-widest text-[#7A726D] uppercase">Allocation</div>
                    <h1 className="font-display text-4xl font-bold text-espresso tracking-tight">Cost shops</h1>
                    <p className="text-sm text-[#7A726D] mt-1">Allocate voucher cost to a department or business unit.</p>
                </div>
                <Button onClick={openCreate} className="bg-espresso hover:bg-[#2c1a17] rounded-sm" data-testid="new-cost-shop-button"><Plus className="w-4 h-4 mr-1"/>Add</Button>
                <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(blank); } }}>
                    <DialogContent className="rounded-sm">
                        <DialogHeader>
                            <DialogTitle className="font-display text-espresso">{editing ? "Edit cost shop" : "New cost shop"}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={onSubmit} className="space-y-3" data-testid="cost-shop-form">
                            <div>
                                <Label className="text-xs uppercase text-[#7A726D]">Name</Label>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-sm mt-1" data-testid="cost-shop-name-input"/>
                            </div>
                            <div>
                                <Label className="text-xs uppercase text-[#7A726D]">Description</Label>
                                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" data-testid="cost-shop-desc-input"/>
                            </div>
                            <Button type="submit" className="bg-espresso w-full rounded-sm" data-testid="cost-shop-submit-button">
                                {editing ? "Save changes" : "Create"}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
            <Card className="border-[#E5E0D8] shadow-none rounded-sm">
                <CardContent className="p-0">
                    <Table data-testid="cost-shops-table">
                        <TableHeader>
                            <TableRow className="bg-[#F9F8F6]">
                                <TableHead>Name</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-[#7A726D] py-8">No cost shops yet</TableCell></TableRow>}
                            {items.map((c) => (
                                <TableRow key={c.id}>
                                    <TableCell className="font-medium">{c.name}</TableCell>
                                    <TableCell className="text-[#7A726D]">{c.description || "—"}</TableCell>
                                    <TableCell className="text-right space-x-2">
                                        <button onClick={() => openEdit(c)} className="text-[#C48464] hover:text-espresso inline-flex" data-testid={`cost-shop-edit-${c.id}`}><Pencil className="w-4 h-4"/></button>
                                        <button onClick={() => onDelete(c.id)} className="text-[#B24A45] inline-flex" data-testid={`cost-shop-delete-${c.id}`}><Trash2 className="w-4 h-4"/></button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
